"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useSession } from "next-auth/react";
import { Link } from "@/i18n/routing";

// ── Constants ─────────────────────────────────────────────────────────────────

const ASPECT_RATIOS = ["1:1", "16:9", "9:16", "4:3", "3:4"] as const;
type AspectRatio = (typeof ASPECT_RATIOS)[number];

const STYLE_IDS = ["white-bg", "bestseller", "luxury", "home-scene", "desk-scene"] as const;
type StyleId = (typeof STYLE_IDS)[number];

const STYLES: { id: StyleId; label: string; description: string; badge?: string; thumbnail: string }[] = [
  { id: "white-bg",   label: "Clean White",  description: "Pure white studio background",     badge: "Best for Amazon", thumbnail: "https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773811416506-kh45klhhqtm.png" },
  { id: "bestseller", label: "Best Seller",  description: "High-contrast hero shot for ads",  badge: "Best for Ads",    thumbnail: "https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773819371121-kuwzeffao4.png" },
  { id: "luxury",     label: "Luxury",       description: "Cinematic premium campaign look",                            thumbnail: "https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773819206166-gj5fbbg4px.png" },
  { id: "home-scene", label: "Home Scene",   description: "Cozy lifestyle home environment",  badge: "Best for Shopify", thumbnail: "https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773811829194-sfnwf3lnwpf.png" },
  { id: "desk-scene", label: "Desk & Table", description: "Clean modern workspace setting",                            thumbnail: "https://pub-740292c103e044bcb88e69ea91c83855.r2.dev/ai-generated/images/2026/03/18/1773812008840-b268no471qe.png" },
];

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 90; // 3 minutes maximum

// ── Status enum (must stay in sync with backend TaskStatus) ──────────────────
type TaskStatus = "queued" | "processing" | "succeeded" | "failed" | "timeout";

// ── State machine ─────────────────────────────────────────────────────────────
// Each phase carries only the data it actually needs — no scattered booleans
type GenState =
  | { phase: "idle" }
  | { phase: "creating_task" }
  | { phase: "polling"; taskId: string; progress: number | null }
  | { phase: "succeeded"; imageUrl: string }
  | { phase: "error"; message: string }
  | { phase: "timeout" };

// ── safeJson — the only way to parse fetch responses in this file ─────────────
// Never throws. Returns { ok: false, error, code, raw } instead of crashing.
async function safeJson(
  resp: Response
): Promise<{ ok: true; data: any } | { ok: false; error: string; code: "NON_JSON_RESPONSE"; raw: string }> {
  const text = await resp.text();
  try {
    return { ok: true, data: JSON.parse(text) };
  } catch {
    console.error("[i2i] JSON parse failed. status:", resp.status, "raw:", text.slice(0, 500));
    return {
      ok: false,
      error: `Non-JSON response (${resp.status})`,
      code: "NON_JSON_RESPONSE",
      raw: text.slice(0, 300),
    };
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ImageToImagePage() {
  const [imageFile, setImageFile]     = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("1:1");
  const [styleId, setStyleId]         = useState<StyleId>("white-bg");
  const [promptAddon, setPromptAddon] = useState("");
  const [genState, setGenState]       = useState<GenState>({ phase: "idle" });
  const [credits, setCredits]         = useState<number | null>(null);

  const { data: session } = useSession();

  // Abort controller ref — allows cancelling in-flight requests on unmount or
  // when the user clicks Generate again before the previous run completes
  const abortRef = useRef<AbortController | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // Fetch credits when session is available
  useEffect(() => {
    if (!session?.user) { setCredits(null); return; }
    fetch("/api/user/account")
      .then((r) => r.json())
      .then((res) => {
        if (res.code === 1000) setCredits(res.data.availablePoints ?? 0);
      })
      .catch(() => {});
  }, [session]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setImageFile(file);
    setGenState({ phase: "idle" });

    if (!file) {
      setImagePreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview((e.target?.result as string) || null);
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!imageFile) {
      setGenState({ phase: "error", message: "Please upload an image first." });
      return;
    }

    if (credits !== null && credits <= 0) {
      setGenState({ phase: "error", message: "no_credits" });
      return;
    }

    // Cancel any previous run (prevents parallel polling)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // ── Phase: creating_task ──────────────────────────────────────────────────
    setGenState({ phase: "creating_task" });

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("aspectRatio", aspectRatio);
    formData.append("styleId", styleId);
    if (promptAddon.trim()) formData.append("promptAddon", promptAddon.trim());

    console.log("[i2i] POST /api/ai/image-to-image");

    let createResp: Response;
    try {
      createResp = await fetch("/api/ai/image-to-image", {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
    } catch (err: any) {
      if (err.name === "AbortError") return;
      console.error("[i2i] create network error:", err.message);
      setGenState({ phase: "error", message: "Network error — please try again." });
      return;
    }

    console.log("[i2i] POST status:", createResp.status);

    const createParsed = await safeJson(createResp);
    console.log("[i2i] POST parsed:", createParsed);

    // Non-JSON (e.g. platform 504 HTML page)
    if (!createParsed.ok) {
      setGenState({ phase: "error", message: createParsed.error });
      return;
    }

    const createBody = createParsed.data;

    // API-level error ({ ok: false, error, code })
    if (!createBody.ok || !createBody.taskId) {
      setGenState({ phase: "error", message: createBody.error || "Failed to create task." });
      return;
    }

    const taskId: string = createBody.taskId;
    console.log("[i2i] taskId:", taskId);

    // ── Phase: polling ────────────────────────────────────────────────────────
    setGenState({ phase: "polling", taskId, progress: null });

    for (let i = 0; i < MAX_POLLS; i++) {
      // Wait before polling (2s interval)
      await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      // Respect abort (page unmount or new Generate click)
      if (controller.signal.aborted) return;

      let statusResp: Response;
      try {
        statusResp = await fetch(`/api/ai/image-to-image/task/${taskId}`, {
          signal: controller.signal,
        });
      } catch (err: any) {
        if (err.name === "AbortError") return;
        console.error(`[i2i] Poll[${i}] network error:`, err.message);
        setGenState({ phase: "error", message: "Network error while polling." });
        return;
      }

      console.log(`[i2i] Poll[${i}] status:`, statusResp.status);

      const statusParsed = await safeJson(statusResp);
      console.log(`[i2i] Poll[${i}] body:`, statusParsed);

      // Non-JSON response
      if (!statusParsed.ok) {
        setGenState({ phase: "error", message: statusParsed.error });
        return;
      }

      const body = statusParsed.data;

      // API-level error (bad taskId, provider down, etc.)
      if (!body.ok) {
        setGenState({ phase: "error", message: body.error || "Generation failed." });
        return;
      }

      const taskStatus: TaskStatus = body.status;

      // Update progress display
      setGenState({
        phase: "polling",
        taskId,
        progress: typeof body.progress === "number" ? body.progress : null,
      });

      if (taskStatus === "succeeded") {
        console.log("[i2i] end=succeeded imageUrl:", body.imageUrl);
        setGenState({ phase: "succeeded", imageUrl: body.imageUrl });
        return;
      }

      if (taskStatus === "failed") {
        console.log("[i2i] end=failed error:", body.error);
        setGenState({ phase: "error", message: body.error || "Generation failed." });
        return;
      }

      // "queued" | "processing" → keep polling
    }

    // Exhausted all poll attempts
    console.log("[i2i] end=timeout after", MAX_POLLS, "polls");
    setGenState({ phase: "timeout" });
  };

  // Derived display helpers
  const isWorking  = genState.phase === "creating_task" || genState.phase === "polling";
  const progress   = genState.phase === "polling" ? genState.progress : null;

  const btnLabel =
    genState.phase === "creating_task" ? "Uploading..."
    : genState.phase === "polling"     ? (progress !== null ? `Processing... ${progress}%` : "Processing...")
    : "Generate";

  return (
    <div className="min-h-screen bg-background">
      <section className="container mx-auto px-4 pt-20 pb-16">
        <div className="max-w-7xl mx-auto">

          <div className="mb-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Image to Image</h1>
            <p className="mt-3 text-muted-foreground">
              Upload a product image and get a professional visual.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* ── Input panel ──────────────────────────────────────────── */}
            <div className="space-y-6 bg-card p-6 rounded-xl border border-border">

              {/* Image upload */}
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Upload Image</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Select a product image as the generation input.
                </p>
                {!imagePreview ? (
                  <label className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    <p className="text-sm text-muted-foreground">Click to upload image</p>
                  </label>
                ) : (
                  <div>
                    <img
                      src={imagePreview}
                      alt="Input preview"
                      className="w-full max-h-[300px] object-contain rounded-lg border border-border"
                    />
                    <label className="mt-2 block text-center text-xs text-muted-foreground cursor-pointer hover:text-primary">
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      Change image
                    </label>
                  </div>
                )}
              </div>

              {/* Aspect ratio */}
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Aspect Ratio</h2>
                <select
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}
                >
                  {ASPECT_RATIOS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Visual style */}
              <div>
                <div className="text-sm font-medium text-foreground mb-3">Visual Style</div>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setStyleId(s.id)}
                      className={[
                        "relative rounded-lg border p-3 text-left transition",
                        styleId === s.id
                          ? "border-foreground/40 bg-foreground/5 ring-1 ring-foreground/20"
                          : "border-border hover:bg-muted/40",
                      ].join(" ")}
                    >
                      {/* Style thumbnail */}
                      <div className="w-full aspect-square rounded-md bg-muted/50 overflow-hidden mb-2">
                        <img
                          src={s.thumbnail}
                          alt={s.label}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      </div>
                      {/* Badge */}
                      {s.badge && (
                        <span className="absolute top-2 right-2 text-[10px] font-medium bg-primary text-primary-foreground rounded px-1.5 py-0.5 leading-none">
                          {s.badge}
                        </span>
                      )}
                      <div className="text-xs font-semibold text-foreground">{s.label}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground leading-tight">{s.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced: prompt addon */}
              <details className="group">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none list-none flex items-center gap-1">
                  <span className="transition-transform group-open:rotate-90">▶</span>
                  Advanced
                </summary>
                <div className="mt-2">
                  <textarea
                    value={promptAddon}
                    onChange={(e) => setPromptAddon(e.target.value)}
                    placeholder="Add custom description, e.g. red packaging, glass bottle, minimalist"
                    rows={2}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </details>

              <Button
                onClick={handleGenerate}
                disabled={!imageFile || isWorking}
                className="w-full"
                size="lg"
              >
                {btnLabel}
              </Button>

              {genState.phase === "error" && genState.message === "no_credits" ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
                  <p className="font-medium text-destructive mb-2">You have no credits left.</p>
                  <Link href="/pricing">
                    <Button size="sm" variant="default">Buy credits</Button>
                  </Link>
                </div>
              ) : genState.phase === "error" ? (
                <p className="text-sm text-red-500">{genState.message}</p>
              ) : null}
              {genState.phase === "timeout" && (
                <p className="text-sm text-red-500">
                  Generation timed out. Please try again.
                </p>
              )}
            </div>

            {/* ── Output panel ─────────────────────────────────────────── */}
            <div className="bg-muted/30 rounded-xl p-8 min-h-[520px] border-2 border-dashed border-border flex items-center justify-center">
              {genState.phase === "creating_task" && (
                <p className="text-muted-foreground">Uploading image...</p>
              )}
              {genState.phase === "polling" && (
                <p className="text-muted-foreground">
                  {genState.progress !== null
                    ? `Processing... ${genState.progress}%`
                    : "Processing..."}
                </p>
              )}
              {genState.phase === "succeeded" && (
                <img
                  src={genState.imageUrl}
                  alt="Generated output"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              )}
              {(genState.phase === "idle" ||
                genState.phase === "error" ||
                genState.phase === "timeout") && (
                <p className="text-muted-foreground text-center">
                  Generated result will appear here.
                </p>
              )}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
